// WHAT: Constants for Miracle Flow step definitions and configurations
// WHY:  Provides centralized step definitions and metadata
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: FlowStep type
// RISKS:
//   - NOTE: Step definitions are hardcoded - may need updates when flow changes
//   - NOTE: Step names and descriptions may not match user expectations

import { type FlowStep } from "../types";

/**
 * WHAT:
 *   Constants file that defines all Miracle Flow steps, their names,
 *   descriptions, and default configurations.
 *
 * WHY:
 *   Centralizes step definitions for consistency across the application.
 *
 * BEHAVIOR:
 *   - Defines step arrays for different session types
 *   - Provides step name mappings
 *   - Provides step description mappings
 *   - Defines default timer durations
 *
 * RISKS:
 *   - NOTE: Step definitions are hardcoded - may need updates when flow changes
 *   - NOTE: Step names and descriptions may not match user expectations
 */

/**
 * Morning session steps
 */
export const MORNING_STEPS: FlowStep[] = [
  "Meditation",
  "Affirmations",
  "Visualization",
  "Exercise",
  "Reading",
  "Scribing",
];

export const EVENING_STEPS: FlowStep[] = [
  "EveningReflection",
  "EveningAffirmations",
  "JournalQuestions",
];

export const MONTHLY_STEPS: FlowStep[] = ["MonthlyCheck"];

export const WEEKLY_STEPS: FlowStep[] = ["WeeklyReflection"];

export const STEP_NAMES: Record<FlowStep, string> = {
  Affirmations: "Affirmations",
  EveningAffirmations: "Evening Affirmations",
  EveningMeditation: "Evening Meditation",
  EveningReflection: "Evening Reflection",
  Exercise: "Exercise",
  JournalQuestions: "Journal Questions",
  Meditation: "Meditation",
  MonthlyCheck: "Monthly Check",
  Reading: "Reading",
  Scribing: "Scribing",
  Visualization: "Visualization",
  WeeklyReflection: "Weekly Reflection",
};

export const STEP_DESCRIPTIONS: Record<FlowStep, string> = {
  Affirmations: "Strengthen your mindset with positive affirmations",
  EveningAffirmations: "End with positive thoughts",
  EveningMeditation: "Evening meditation for relaxation",
  EveningReflection: "Reflect on your day",
  Exercise: "Energize your body with movement",
  JournalQuestions: "Document your daily insights",
  Meditation: "Start your day with mindful meditation",
  MonthlyCheck: "Reflect on your month and rate important life areas",
  Reading: "Feed your mind with knowledge",
  Scribing: "Reflect and plan your day",
  Visualization: "Visualize your goals and dreams",
  WeeklyReflection: "Reflect on your week and plan ahead",
};

export const DEFAULT_TIMER_DURATIONS: Record<FlowStep, number> = {
  Affirmations: 300, // 5 minutes
  EveningAffirmations: 300, // 5 minutes
  EveningMeditation: 600, // 10 minutes
  EveningReflection: 600, // 10 minutes
  Exercise: 1200, // 20 minutes
  JournalQuestions: 600, // 10 minutes
  Meditation: 600, // 10 minutes
  MonthlyCheck: 1200, // 20 minutes
  Reading: 1200, // 20 minutes
  Scribing: 600, // 10 minutes
  Visualization: 300, // 5 minutes
  WeeklyReflection: 900, // 15 minutes
};

export const STEP_ICONS: Record<FlowStep, string> = {
  Affirmations: "💪",
  EveningAffirmations: "✨",
  EveningMeditation: "🌙",
  EveningReflection: "🌅",
  Exercise: "🏃",
  JournalQuestions: "📝",
  Meditation: "🧘",
  MonthlyCheck: "📆",
  Reading: "📚",
  Scribing: "✍️",
  Visualization: "👁️",
  WeeklyReflection: "📅",
};

export const MONTHLY_CATEGORIES = [
  "Gesamtstimmung",
  "Dankbarkeit",
  "Achtsamkeit",
  "Familie",
  "Freunde",
  "Partnerschaft",
  "Spaß",
  "Ruhe & Gelassenheit",
  "Zeit für dich",
  "Gesund essen",
  "Wasser trinken",
  "Sport",
  "Nach draußen gehen",
  "Gesundheit",
  "Kreativität",
  "Finanzen",
  "Arbeit & Ausbildung",
  "Gedanken & Emotionen",
  "Die Gegenwart",
  "Die Zukunft",
];

export const SESSION_COLORS = {
  evening: {
    accent: "#5B21B6",
    primary: "#7C3AED",
    secondary: "#EDE9FE",
  },
  morning: {
    accent: "#D97706",
    primary: "#F59E0B",
    secondary: "#FEF3C7",
  },
};
